import React, { useState, useEffect, useCallback } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { apiCallGeneric } from "../../services/api-service";
import { ReactComponent as DownArrow } from "../../assets/svg/down_arror.svg"; // your custom arrow

import { X } from "lucide-react";
import { sanitizeInput } from "./security-utils";

/**
 * SearchableDropdown Component
 * A reusable single-select dropdown component with asynchronous fetching and VAPT hardening.
 */
const SearchableDropdown = ({
  apiUrl,
  placeholder,
  value,
  onSelect,
  handleClear,
}) => {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch data using centralized api-service
  const fetchData = useCallback(async (searchTerm = "") => {
    if (!apiUrl) return;

    setLoading(true);
    try {
      if (apiUrl) {
        const response = await apiCallGeneric("GET", apiUrl, null, {
          params: { term: search },
        });

      if (response && response.status === 200 && response.data.success) {
        setOptions(response.data.suggestionlist || []);
      } else {
        setOptions([]);
      }
    }
    } catch (err) {
      // Error is handled by api-service internally
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Initial fetch on mount
  useEffect(() => {
    fetchData("");
  }, [fetchData]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData(search);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search, fetchData]);

  return (
    <Dropdown style={{ width: "100%" }}>
      <Dropdown.Toggle
        variant="light"
        id="dropdown-sort"
        className="d-flex align-items-center justify-content-between w-100 dp-toggle"
        style={{
          borderRadius: "12px",
          border: "1px solid #EBEBEB",
          padding: "6px 12px",
          backgroundColor: "white",
          height: "40px",
        }}
      >
        <span className="fs-12 fw-400 textGrey text-truncate">
          {placeholder} :
        </span>
        <span className="d-flex align-items-center px-1 overflow-hidden flex-grow-1 justify-content-end">
          <span
            className="dropdown-text text-truncate me-1"
            title={value?.label || "Select"}
            style={{ fontWeight: "500", color: value?.label ? "#212129" : "#888888" }}
          >
            {value?.label ? sanitizeInput(String(value.label)) : "Select"}
          </span>

          {value?.label && (
            <X
              size={16}
              className="ms-1 cursor-pointer text-muted"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="w-100 shadow-sm border-0"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #EBEBEB",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          minWidth: "200px",
          overflowY: "auto",
          maxHeight: "220px",
          marginTop: "4px",
        }}
      >
        <div style={{ padding: "8px", position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
          <input
            type="text"
            className="fs-14 fw-500 textcolor"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${placeholder}...`}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: "8px",
              border: "1px solid #EBEBEB",
              outline: "none",
            }}
          />
        </div>

        {loading ? (
          <Dropdown.Item disabled className="fs-13 text-center text-muted py-3">Loading...</Dropdown.Item>
        ) : options.length === 0 ? (
          <Dropdown.Item disabled className="fs-13 text-center text-muted py-3">No results found</Dropdown.Item>
        ) : (
          options.map((option) => (
            <Dropdown.Item
              className="fs-14 fw-500 textcolor py-2"
              key={option.value}
              onClick={() => onSelect(option)}
              active={value?.value === option.value}
            >
              {sanitizeInput(String(option.label || ""))}
            </Dropdown.Item>
          ))
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SearchableDropdown;
